import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, toast } from '@mlm/ui';
import {
  fulfillBranchOrder,
  getBranchOrder,
  listBranchOrders,
  type BranchOrderDetail,
} from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { hasPerm } from '../lib/auth-store';
import { posStatusVariant } from '../lib/pos-status';
import { salesOrderSeries } from '../lib/sales-order';
import { BranchPicker } from '../components/branch-picker';
import { printPackingSlip } from '../lib/packing-slip';

const STATUSES = [
  '',
  'to_pay',
  'paid',
  'to_pick',
  'to_ship',
  'shipping',
  'delivered',
  'to_release',
  'completed',
  'cancelled',
  'refunded',
] as const;

const TYPES = [
  { value: '', label: 'All types' },
  { value: 'ecom', label: 'Online (ecom)' },
  { value: 'pos', label: 'POS' },
  { value: 'slot_activation', label: 'Slot activation' },
] as const;

const TYPE_LABELS: Record<string, string> = {
  ecom: 'Online',
  pos: 'POS',
  slot_activation: 'Slot',
};

/**
 * Cashier Orders tab — a branch-scoped port of the admin Orders view. The
 * server forces the branch from the cashier's session, so a scoped cashier only
 * ever sees their own branch. Cancel/refund/payment approval are NOT here —
 * those stay admin-only; a cashier can only advance fulfilment.
 */
export function OrdersPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const orderPrefix = branches.find((b) => b.id === branchId)?.orderPrefix ?? null;
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['pos-orders', branchId, status, type, q, from, to],
    enabled: !!branchId,
    queryFn: () =>
      listBranchOrders({
        branchId: branchId!,
        status: status || undefined,
        type: type || undefined,
        q: q || undefined,
        dateFrom: from ? new Date(from).toISOString() : undefined,
        dateTo: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
        pageSize: 200,
      }),
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Orders</CardTitle>
          <BranchPicker branches={branches} branchId={branchId} isScoped={isScoped} onChange={setSelectedBranchId} />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Search</label>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Order #, buyer, slot, tracking"
                className="w-56"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Type</label>
              <select
                className="h-10 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Status</label>
              <select
                className="h-10 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s === '' ? 'All' : s}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setStatus(''); setType(''); setQ(''); setFrom(''); setTo(''); }}
              disabled={!status && !type && !q && !from && !to}
            >
              Clear
            </Button>
          </div>

          {!branchId ? (
            <p className="text-sm text-muted-foreground">Pick a branch to see its orders.</p>
          ) : query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !query.data?.rows.length ? (
            <p className="text-sm text-muted-foreground">No orders match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-2 py-1.5">When</th>
                  <th className="px-2 py-1.5">Order</th>
                  <th className="px-2 py-1.5">Buyer</th>
                  <th className="px-2 py-1.5">Slot</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {query.data.rows.map((o) => (
                  <tr
                    key={o.orderId}
                    onClick={() => setSelected(o.orderId)}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">
                      {new Date(o.createdAt).toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">#{salesOrderSeries(o.code, orderPrefix)}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {o.buyerName ?? o.buyerEmail ?? '—'}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{o.slotCode ?? '—'}</td>
                    <td className="px-2 py-1.5 text-xs">{TYPE_LABELS[o.type] ?? o.type}</td>
                    <td className="px-2 py-1.5">
                      <Badge variant={posStatusVariant(o.status)}>{o.status}</Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {o.paymentCurrency === 'cash' ? '₱' : ''}
                      {Number(o.total).toFixed(2)}{' '}
                      {o.paymentCurrency !== 'cash' ? o.paymentCurrency.toUpperCase() : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <OrderDetailDrawer orderId={selected} orderPrefix={orderPrefix} onClose={() => setSelected(null)} />
    </>
  );
}

// Fulfilment ladder the cashier may drive — cancel/refund/paid are excluded
// even if the state machine would allow them for the admin actor.
const FULFILL_ALLOWED = new Set(['to_ship', 'to_pick', 'shipping', 'delivered', 'completed']);
const EDGE_LABELS: Record<string, string> = {
  to_ship: 'Mark ready to ship',
  to_pick: 'Mark ready for pickup',
  shipping: 'Mark shipped',
  delivered: 'Mark delivered',
  completed: 'Mark completed',
};

function OrderDetailDrawer({
  orderId,
  orderPrefix,
  onClose,
}: {
  orderId: string | null;
  orderPrefix: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [tracking, setTracking] = useState('');
  const canFulfill = hasPerm('pos.orders.fulfill');

  const query = useQuery({
    queryKey: ['pos-order-detail', orderId],
    enabled: !!orderId,
    queryFn: () => getBranchOrder(orderId!),
  });

  const fulfill = useMutation({
    mutationFn: (status: string) =>
      fulfillBranchOrder(orderId!, {
        status,
        trackingNumber: status === 'shipping' && tracking.trim() ? tracking.trim() : undefined,
      }),
    onSuccess: () => {
      toast.success('Order updated');
      setTracking('');
      void qc.invalidateQueries({ queryKey: ['pos-order-detail', orderId] });
      void qc.invalidateQueries({ queryKey: ['pos-orders'] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      toast.error(e.response?.data?.message ?? e.response?.data?.error ?? 'Could not update order');
    },
  });

  const order = query.data;
  const fulfillEdges = (order?.fulfillEdges ?? []).filter((e) => FULFILL_ALLOWED.has(e.to));

  return (
    <Dialog open={!!orderId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {order ? `Order #${salesOrderSeries(order.code, orderPrefix)}` : 'Order'}
          </DialogTitle>
        </DialogHeader>

        {query.isLoading || !order ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge variant={posStatusVariant(order.status)}>{order.status}</Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Buyer</p>
                <p>{order.buyerName ?? order.buyerEmail ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="tabular-nums">
                  {order.paymentCurrency === 'cash' ? '₱' : ''}
                  {Number(order.total).toFixed(2)}{' '}
                  {order.paymentCurrency !== 'cash' ? order.paymentCurrency.toUpperCase() : ''}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Items</p>
              <table className="w-full text-sm">
                <tbody>
                  {order.items.map((it, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1">
                        {it.name}
                        {it.variantName ? <span className="text-muted-foreground"> · {it.variantName}</span> : null}
                      </td>
                      <td className="py-1 text-right tabular-nums text-muted-foreground">×{it.qty}</td>
                      <td className="py-1 text-right tabular-nums">{Number(it.lineTotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {order.trackingNumber && (
              <p className="text-xs">
                <span className="text-muted-foreground">Tracking: </span>
                <span className="font-mono">{order.trackingNumber}</span>
              </p>
            )}

            {/* Shipping requires a tracking number; the state machine enforces it too. */}
            {canFulfill && fulfillEdges.some((e) => e.to === 'shipping') && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Tracking number (for shipping)</label>
                <Input
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="e.g. JT1234567890"
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {order && (
            <Button variant="outline" onClick={() => printPackingSlip(order)}>
              Packing slip
            </Button>
          )}
          {order && canFulfill &&
            fulfillEdges.map((e) => (
              <Button
                key={e.to}
                onClick={() => fulfill.mutate(e.to)}
                disabled={fulfill.isPending || (e.to === 'shipping' && !tracking.trim())}
              >
                {EDGE_LABELS[e.to] ?? e.to}
              </Button>
            ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
